
import React, { createContext, useContext, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { setLoading } from 'redux/actions/app';
import moment from 'moment';
import { store } from 'react-notifications-component';
import * as API from 'services/api';
import { RemoveModal } from 'components';
import FileUploadModal from 'components/FileUploadModal';
import { appProfileSelector } from 'redux/selectors';
import { authSelector } from 'redux/selectors';
import {
    downloadSingleDocument,
    previewSingleDocument,
} from 'pages/DocumentsApave/documentsDownloadUtils';

export default function useHomeContext() {
    const context = useContext(HomeContext);
    if (!context) {
        throw new Error('useHomeContext must be used within a HomeProvider');
    }
    return context;
}

const HomeContext = createContext();

export const HomeProvider = ({ children }) => {
    const dispatch = useDispatch();
    const profile = useSelector(appProfileSelector);
    const { accessToken, tokenType } = useSelector(authSelector);

    const onDirectDownload = ({ ged, title, documentDate, productId, type }) => async e => {
        if (!ged) {
            return;
        }

        dispatch(setLoading(true));
        try {
            const filename = productId
                ? `${type}_${moment(documentDate).format('DDMMYYYY')}_${productId}`
                : title;
            await downloadSingleDocument({
                doc: { ged, title: filename },
                profile,
                accessToken,
                tokenType,
            });
        } catch (err) {
            console.log(err);
        } finally {
            dispatch(setLoading(false));
        }
    };

    const onDownload = (ged) => e => {
        if (!ged) {
            return;
        }
        previewSingleDocument({
            doc: { ged },
            profile,
            accessToken,
            tokenType,
        }).catch((err) => {
            console.error('Error previewing PDF:', err);
        });
    };

    const onDiscoverFile = (ged, link) => {
        previewSingleDocument({
            doc: { ged, link },
            profile,
            accessToken,
            tokenType,
        }).catch((err) => {
            console.error('Error previewing PDF:', err);
            if (!ged && link) {
                window.open(link, '_blank');
            }
        });
    };

    const onDownloadFile = async file => {
        dispatch(setLoading(true));
        try {
            if (file.ged) {
                await downloadSingleDocument({
                    doc: { ged: file.ged, title: file.title },
                    profile,
                    accessToken,
                    tokenType,
                });
                return;
            }

            const url = file.privateUrl;
            const splitnames = (file.Key || '').split('/');
            const filename = splitnames[splitnames.length - 1] || `${file.title}.pdf`;

            const response = await fetch(url, {
                method: 'GET',
                headers: accessToken ? { Authorization: `${tokenType} ${accessToken}` } : {},
            });
            if (!response.ok) {
                throw new Error(`HTTP_${response.status}`);
            }
            const buffer = await response.arrayBuffer();
            const blob = new Blob([buffer], { type: 'application/pdf' });
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.log(err);
        } finally {
            dispatch(setLoading(false));
        }
    };

    const [importUsersModalVisible, setImportUsersModalVisible] = useState(false);

    const [duplicated, setDuplicated] = useState();

    const toggleUsersImportModal = () => {
        setImportUsersModalVisible(false);
    };
    const onImportTrainees = (file, type, forced) => {
        setDuplicated();
        dispatch(setLoading(true));
        const formData = new FormData();
        formData.append('type', type);
        formData.append('file', file);
        if (forced)
            formData.append('forced', forced);

        API.importTraines(formData)
            .then(res => {
                dispatch(setLoading(false));
                if (res.code === 201) {
                    setDuplicated({ file, type });
                } else if (res.code === 200) {
                    window.dataLayer.push({
                        'event': 'action',
                        'click_name': 'IMPORTER FORMATION AUTRE ORGANISME',
                        'action_type': 'IMPORT',
                    });
                    toggleUsersImportModal();
                    store.addNotification({
                        message: "uploaded successfully.",
                        type: 'success',
                        container: 'top-right',
                        animationIn: ['animated', 'fadeIn'],
                        animationOut: ['animated', 'fadeOut'],
                        dismiss: {
                            duration: 5000,
                            onScreen: true,
                        },
                    });
                } else {
                    store.addNotification({
                        message: res.message,
                        type: 'danger',
                        container: 'top-right',
                        animationIn: ['animated', 'fadeIn'],
                        animationOut: ['animated', 'fadeOut'],
                        dismiss: {
                            duration: 5000,
                            onScreen: true,
                        },
                    });
                }
            })
            .catch(err => {
                console.log(err)
                dispatch(setLoading(false));
            });
    };

    const values = useMemo(() => ({
        onDirectDownload,
        onDownload,
        onDiscoverFile,
        onDownloadFile,

        setImportUsersModalVisible
    }), [
        accessToken,
        tokenType,
        profile
    ]);
    return (
        <HomeContext.Provider
            value={values}
        >
            {children}
            <FileUploadModal
                addFree={true}
                visible={importUsersModalVisible}
                onConfirm={onImportTrainees}
                onClose={toggleUsersImportModal}
            />
            {duplicated && (
                <RemoveModal
                    visible={!!duplicated}
                    onClose={() => setDuplicated()}
                    onConfirm={() => {
                        onImportTrainees(duplicated.file, duplicated.type, true)
                    }}
                    loading={false}
                    title="Confirm"
                    description={"Cette session semble avoir déjà été importée, souhaitez vous continuer ?"}
                />
            )}
        </HomeContext.Provider>
    );
};

HomeProvider.propTypes = {
    children: PropTypes.any,
};
